import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface Setting {
  readonly parameterCode: string;
  readonly value: string;
  readonly valueType: string;
  readonly description: string | null;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly defaultValue: string;
  readonly isDefault: boolean;
  readonly group: string;
  readonly modifiedAt: string | null;
  /** Family tab only: tenants whose own row disagrees right now. */
  readonly divergentTenants?: number;
  /** Family tab only: tenants written on the last save. */
  readonly tenantsUpdated?: number;
}

const BASE = '/v1/platform/password-policy';

/** 0 is how the policy has always said "never expires"; the screen says it in
 *  words and keeps the number for how long. */
const EXPIRY_CODE = 'PWD_EXPIRY_DAYS';

const MUTED = 'var(--color-text-muted, #656d76)';
const BORDER = '1px solid var(--color-border-default, #d0d7de)';

type Tab = 'platform' | 'tenant-family';

/**
 * Two populations, two tabs.
 *
 * A single list holding the platform administrator beside the tenants let an
 * administrator set their own sign-in policy believing they were setting a
 * customer's - and two of those rows carried the same display name, so the
 * code was the only thing telling them apart. Which tenant is the platform one
 * is read from t001_tenant.is_platform on the server, never named here.
 */
const TABS: ReadonlyArray<{ id: Tab; label: string; blurb: string }> = [
  {
    id: 'platform',
    label: 'Platform administrators',
    blurb:
      'Rules for the accounts that administer every tenant \u2014 including the one you are signed in with. They do not affect any tenant\u2019s own users.',
  },
  {
    id: 'tenant-family',
    label: 'Tenant family',
    blurb:
      'One rule for every tenant. Saving applies it to all tenants running today and to any tenant created afterwards. The platform administrators are not affected.',
  },
];

export function PasswordPolicyPage(): ReactNode {
  const { token } = useAuth();
  const [tab, setTab] = useState<Tab>('platform');
  const [rows, setRows] = useState<readonly Setting[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(async (which: Tab) => {
    setError(null);
    setStatus(null);
    setRows(null);
    try {
      const r = await apiRequest<readonly Setting[]>(`${BASE}/${which}`, { token });
      setRows(r);
      setDraft(Object.fromEntries(r.map((s) => [s.parameterCode, s.value])));
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(tab); }, [tab, load]);

  const save = useCallback(async (code: string) => {
    setBusy(code); setError(null); setStatus(null);
    try {
      const updated = await apiRequest<Setting>(`${BASE}/${tab}/${code}`, {
        method: 'PUT', token, body: { value: draft[code] ?? '' },
      });
      setRows((prev) => (prev ?? []).map((r) => (r.parameterCode === code ? updated : r)));
      setDraft((d) => ({ ...d, [code]: updated.value }));
      setStatus(
        updated.tenantsUpdated !== undefined
          ? `${code} saved \u2014 applied to ${updated.tenantsUpdated} tenant(s)`
          : `${code} saved`,
      );
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }, [tab, token, draft]);

  const active = TABS.find((t) => t.id === tab) ?? TABS[0];
  const expiry = (rows ?? []).find((r) => r.parameterCode === EXPIRY_CODE);
  const expiryDays = Number(draft[EXPIRY_CODE] ?? expiry?.value ?? '0');
  const expiryOn = Number.isFinite(expiryDays) && expiryDays > 0;
  const expiryDirty = expiry ? (draft[EXPIRY_CODE] ?? expiry.value) !== expiry.value : false;
  const others = (rows ?? []).filter((r) => r.parameterCode !== EXPIRY_CODE);
  const drifted = (rows ?? []).filter((r) => (r.divergentTenants ?? 0) > 0);

  return (
    <main aria-label="password-policy">
      <h1>Password Management</h1>

      <div
        role="tablist"
        aria-label="policy-scope"
        style={{ display: 'flex', gap: 4, borderBottom: BORDER, margin: '16px 0 0' }}
      >
        {TABS.map((t) => {
          const on = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={on}
              aria-controls="policy-panel"
              onClick={() => setTab(t.id)}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderBottom: on
                  ? '2px solid var(--color-accent, #1f6feb)'
                  : '2px solid transparent',
                background: 'none',
                fontWeight: on ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div id="policy-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        <p style={{ color: MUTED, marginTop: 12 }}>{active.blurb}</p>

        {error ? <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
        {status ? <p role="status">{status}</p> : null}

        {tab === 'tenant-family' && drifted.length > 0 ? (
          <p aria-label="drift-notice" style={{ fontSize: 13, color: MUTED, border: BORDER, padding: '8px 12px' }}>
            {drifted.length} rule(s) are set differently on some tenants than the
            family value shown here. Saving a rule brings every tenant back onto it.
          </p>
        ) : null}

        {!rows ? (error ? null : <p>Loading rules…</p>) : rows.length === 0 ? (
          <p aria-label="empty">No password rules are on record here.</p>
        ) : (
          <>
            {expiry ? (
              <section aria-label="expiry" style={{ margin: '20px 0 28px', paddingBottom: 16, borderBottom: BORDER }}>
                <h2 style={{ fontSize: 16, marginBottom: 8 }}>Password expiry</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <label htmlFor="expiry-enabled">Passwords expire</label>
                  {/*
                    Staged, not written on change. This used to save the moment
                    the dropdown moved, while every other control waits for Save,
                    so one stray click switched expiry off and nothing asked.
                  */}
                  <select
                    id="expiry-enabled"
                    aria-label="expiry-enabled"
                    value={expiryOn ? 'YES' : 'NO'}
                    onChange={(e) => {
                      const on = e.target.value === 'YES';
                      const next = on
                        ? (expiry.defaultValue === '0' ? '90' : expiry.defaultValue)
                        : '0';
                      setDraft((d) => ({ ...d, [EXPIRY_CODE]: next }));
                    }}
                  >
                    <option value="YES">Yes</option>
                    <option value="NO">No</option>
                  </select>

                  {expiryOn ? (
                    <>
                      <label htmlFor="expiry-days">after</label>
                      <input
                        id="expiry-days"
                        aria-label={`value-${EXPIRY_CODE}`}
                        type="number"
                        min={expiry.minValue ?? undefined}
                        max={expiry.maxValue ?? undefined}
                        value={draft[EXPIRY_CODE] ?? expiry.value}
                        onChange={(e) => setDraft((d) => ({ ...d, [EXPIRY_CODE]: e.target.value }))}
                        style={{ width: 100 }}
                      />
                      <span>days</span>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: MUTED }}>
                      Passwords will never expire.
                    </span>
                  )}

                  <button
                    type="button"
                    aria-label={`save-${EXPIRY_CODE}`}
                    disabled={!expiryDirty || busy === EXPIRY_CODE}
                    onClick={() => void save(EXPIRY_CODE)}
                  >
                    {busy === EXPIRY_CODE ? 'Saving…' : 'Save'}
                  </button>

                  {expiryOn ? (
                    <span style={{ fontSize: 12, color: MUTED }}>
                      {expiry.minValue ?? '—'} to {expiry.maxValue ?? '—'}
                    </span>
                  ) : null}
                  {expiryDirty ? <span style={{ fontSize: 12, color: MUTED }}>Not saved yet</span> : null}
                </div>
              </section>
            ) : null}

            <section aria-label="rules">
              <h2 style={{ fontSize: 16, marginBottom: 8 }}>Rules</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 4px' }}>Rule</th>
                    <th style={{ padding: '8px 4px' }}>Value</th>
                    <th style={{ padding: '8px 4px' }}>Allowed</th>
                    <th style={{ padding: '8px 4px' }}>Default</th>
                    <th style={{ padding: '8px 4px' }} />
                  </tr>
                </thead>
                <tbody>
                  {others.map((s) => {
                    const dirty = (draft[s.parameterCode] ?? s.value) !== s.value;
                    return (
                      <tr key={s.parameterCode} aria-label={`row-${s.parameterCode}`} style={{ borderBottom: BORDER }}>
                        <td style={{ padding: '8px 4px' }}>
                          <div style={{ fontWeight: 600 }}>{s.parameterCode}</div>
                          {s.description ? (
                            <div style={{ fontSize: 12, color: MUTED }}>{s.description}</div>
                          ) : null}
                          {(s.divergentTenants ?? 0) > 0 ? (
                            <div style={{ fontSize: 12, color: MUTED }}>
                              {s.divergentTenants} tenant(s) currently differ
                            </div>
                          ) : null}
                        </td>
                        <td style={{ padding: '8px 4px' }}>
                          {s.valueType === 'BOOLEAN' ? (
                            <select
                              aria-label={`value-${s.parameterCode}`}
                              value={draft[s.parameterCode] ?? s.value}
                              onChange={(e) => setDraft((d) => ({ ...d, [s.parameterCode]: e.target.value }))}
                            >
                              <option value="TRUE">Yes</option>
                              <option value="FALSE">No</option>
                            </select>
                          ) : (
                            <input
                              aria-label={`value-${s.parameterCode}`}
                              type={s.valueType === 'INT' || s.valueType === 'DECIMAL' ? 'number' : 'text'}
                              min={s.minValue ?? undefined}
                              max={s.maxValue ?? undefined}
                              value={draft[s.parameterCode] ?? s.value}
                              onChange={(e) => setDraft((d) => ({ ...d, [s.parameterCode]: e.target.value }))}
                              style={{ width: 160 }}
                            />
                          )}
                        </td>
                        <td style={{ padding: '8px 4px', fontSize: 12, color: MUTED }}>
                          {s.minValue != null || s.maxValue != null
                            ? `${s.minValue ?? '—'} to ${s.maxValue ?? '—'}`
                            : s.valueType === 'BOOLEAN' ? 'Yes or No' : s.valueType}
                        </td>
                        <td style={{ padding: '8px 4px', fontSize: 12 }}>
                          {s.valueType === 'BOOLEAN'
                            ? (s.defaultValue === 'TRUE' ? 'Yes' : 'No')
                            : s.defaultValue}
                          {s.isDefault ? '' : ' (changed)'}
                        </td>
                        <td style={{ padding: '8px 4px' }}>
                          <button
                            type="button"
                            aria-label={`save-${s.parameterCode}`}
                            disabled={!dirty || busy === s.parameterCode}
                            onClick={() => void save(s.parameterCode)}
                          >
                            {busy === s.parameterCode ? 'Saving…' : 'Save'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
